import type React from 'react';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import AppointmentCalendar from './AppointmentCalendar';
import BookingStatusModal from './BookingStatusModalProps';
import logo from '../assets/images/Appointment System/logo.png';
import Background from '../assets/images/Appointment System/background.png';
import { X, Mail, ArrowLeft } from 'lucide-react';
import soldier from '../assets/images/Appointment System/soldier.png';

import type {
  SlotItem,
  FetchSlotsResponse,
} from '../services/appointmentSlotService';
import { fetchSlots, prebookSlot } from '../services/appointmentSlotService';
import { createAppointment } from '../services/appointmentService';

const COMPANY = 'aerocomp';

interface TimeSlot {
  time: string;
  status: 'available' | 'booked';
  id: string;
}

interface BookingData {
  date: string;
  time: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  industry: string;
  companyEmail: string;
  companyPhone: string;
  notes: string;
}

export default function AppointmentSystemLanding() {
  const [activeSection, setActiveSection] = useState<'book' | 'email'>('book');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [prebooking, setPrebooking] = useState(false);
  const [booking, setBooking] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [showReceipt, setShowReceipt] = useState(false);
  const [bookingData, setBookingData] = useState<BookingData | null>(null);
  const [showForm, setShowForm] = useState(false);

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [bookingStatus, setBookingStatus] = useState<'success' | 'failed'>(
    'success',
  );
  const [statusMessage, setStatusMessage] = useState('');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    companyEmail: '',
    companyPhone: '',
    notes: '',
  });

  // ---------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------
  const formatSlotLabel = (slot: string) => {
    // slot format: "HH:MM-HH:MM"
    const [start, end] = slot.split('-');
    const to12Hour = (t: string) => {
      const [hStr, mStr] = t.split(':');
      let h = parseInt(hStr, 10);
      const ampm = h >= 12 ? 'PM' : 'AM';
      if (h === 0) h = 12;
      else if (h > 12) h -= 12;
      return `${h}:${mStr} ${ampm}`;
    };
    return `${to12Hour(start)} - ${to12Hour(end)}`;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const loadSlots = async (date: string) => {
    setLoading(true);
    try {
      const apiSlots = (await fetchSlots(COMPANY, date)) as FetchSlotsResponse;

      console.log('FETCHED SLOTS RAW:', apiSlots);

      // Normalize any backend format into an array
      const list: SlotItem[] = Array.isArray(apiSlots)
        ? apiSlots
        : Array.isArray(apiSlots?.data)
          ? apiSlots.data
          : Array.isArray(apiSlots?.slots)
            ? apiSlots.slots
            : Array.isArray(apiSlots?.results)
              ? apiSlots.results
              : Array.isArray(apiSlots?.items)
                ? apiSlots.items
                : [];

      console.log('NORMALIZED LIST:', list);

      const mapped: TimeSlot[] = list.map((slot) => ({
        id: slot._id,
        time: formatSlotLabel(slot.slot),
        status: slot.isBooked ? 'booked' : 'available',
      }));

      setTimeSlots(mapped);
      setSelectedSlot('');
    } catch (error) {
      console.error('Failed to load slots', error);
      setTimeSlots([]);
      toast.error('Failed to load slots. Please try another date.', {
        position: 'top-center',
      });
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------
  // Effects
  // ---------------------------------------------------------
  useEffect(() => {
    if (selectedDate) {
      loadSlots(selectedDate);
    }
  }, [selectedDate]);

  // ---------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------
  const handleSectionChange = (section: 'book' | 'email') => {
    setActiveSection(section);
    setShowForm(false);
    setSelectedDate('');
    setSelectedSlot('');
    setTimeSlots([]);
  };

  const handleDateClick = (dateStr: string) => {
    setSelectedDate(dateStr);
  };

  const handleSlotClick = (slotId: string, status: string) => {
    if (status === 'available') {
      setSelectedSlot(slotId);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // C option: prebookSlot BEFORE showing the form
  const handleContinue = async () => {
    if (!selectedSlot) return;
    if (!selectedDate) {
      toast.error('Please select a date first.', { position: 'top-center' });
      return;
    }

    setPrebooking(true);
    try {
      await prebookSlot(COMPANY, {
        slotId: selectedSlot,
        date: selectedDate,
      });

      setShowForm(true);
    } catch (error: any) {
      console.error('[handleContinue] prebookSlot error:', error);
      const msg =
        error?.message ||
        error?.error ||
        'This slot is no longer available. Please choose another.';
      toast.error(msg, { position: 'top-center' });

      if (selectedDate) {
        await loadSlots(selectedDate); // refresh slot statuses
      }
    } finally {
      setPrebooking(false);
    }
  };

  const handleGoBack = () => {
    setShowForm(false);
  };

  const validateForm = () => {
    const requiredFields = [
      { name: 'firstName', label: 'First Name' },
      { name: 'lastName', label: 'Last Name' },
      { name: 'email', label: 'Email' },
      { name: 'phone', label: 'Phone' },
      { name: 'company', label: 'Company Name' },
      { name: 'industry', label: 'Industry Field' },
      { name: 'companyEmail', label: 'Company Email' },
      { name: 'companyPhone', label: 'Company Phone' },
    ];

    for (const field of requiredFields) {
      if (!formData[field.name as keyof typeof formData].trim()) {
        toast.error(`Please fill in ${field.label}`, {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        return false;
      }
    }
    return true;
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    // Simulate async operation (you can remove setTimeout if not needed)
    await new Promise((resolve) => setTimeout(resolve, 500));

    const selectedSlotData = timeSlots.find((slot) => slot.id === selectedSlot);

    setBookingData({
      date: formatDate(selectedDate),
      time: selectedSlotData?.time || '',
      ...formData,
    });

    setShowReceipt(true);
    setSubmitting(false);
  };

  const handleOpenGmail = () => {
    const recipient = 'aerocompproject@gmail.com';
    const subject = encodeURIComponent('Inquiry');
    const body = encodeURIComponent(
      'Hello Aerocomp Team,\n\nI would like to inquire about your services and products.\n\nBest regards,',
    );

    window.open(
      `https://mail.google.com/mail/?view=cm&fs=1&to=${recipient}&su=${subject}&body=${body}`,
      '_blank',
    );
  };

  // C option (second part): prebookSlot AGAIN + createAppointment
  const handleBookIt = async () => {
    if (!bookingData || !selectedDate || !selectedSlot) {
      toast.error('Missing booking information. Please try again.', {
        position: 'top-center',
      });
      return;
    }

    setBooking(true);
    try {
      // Final check: is slot still free?
      await prebookSlot(COMPANY, {
        slotId: selectedSlot,
        date: selectedDate,
      });

      const selectedSlotData = timeSlots.find(
        (slot) => slot.id === selectedSlot,
      );

      const payload = {
        clientName: `${bookingData.firstName} ${bookingData.lastName}`,
        email: bookingData.email,
        phone: bookingData.phone,

        clientCompany: bookingData.company,
        industry: bookingData.industry,
        companyEmail: bookingData.companyEmail,
        companyPhone: bookingData.companyPhone,

        notes: bookingData.notes || undefined, // free-text ONLY

        appointmentDate: selectedDate,
        slot: selectedSlotData?.time,
        slotId: selectedSlotData?.id,
      };

      await createAppointment(COMPANY, payload);

      setShowReceipt(false);
      setBookingStatus('success');
      setStatusMessage(
        'Your appointment has been successfully booked! You will receive a confirmation email shortly.',
      );
      setShowStatusModal(true);

      // Reset all inputs
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        industry: '',
        companyEmail: '',
        companyPhone: '',
        notes: '',
      });
      setSelectedDate('');
      setSelectedSlot('');
      setShowForm(false);
      setTimeSlots([]);
    } catch (error: any) {
      console.error('Booking failed:', error);

      const msg =
        error?.message ||
        error?.error ||
        'We encountered an error while processing your booking. Please try again or contact support.';

      setShowReceipt(false);
      setBookingStatus('failed');
      setStatusMessage(msg);
      setShowStatusModal(true);
    } finally {
      setBooking(false);
    }
  };

  return (
    <>
      {/* Fixed Background Layer */}
      <div
        className="fixed inset-0 "
        style={{
          backgroundImage: `url(${Background})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
        }}
      />

      {/* Content Container */}
      <div className="relative flex min-h-screen flex-col items-center justify-start px-3 py-4 pt-20 sm:px-4 sm:py-6 md:px-6 md:py-8 lg:px-8 lg:pt-28 xl:px-10">
        <div className="w-full max-w-sm rounded-xl border border-gray-800 bg-black/90 p-4 shadow-2xl backdrop-blur-sm transition-all duration-500 ease-in-out sm:max-w-xl sm:p-5 md:max-w-2xl md:p-6 lg:max-w-4xl lg:p-7 xl:max-w-5xl 2xl:max-w-6xl">
          <div className="rounded-xl p-2 sm:p-3 md:p-4">
            {/* Header */}
            <div className="mb-4 text-center sm:mb-5 md:mb-6">
              <div
                data-aos="fade-up"
                className="flex items-center justify-center gap-2"
              >
                <img
                  src={logo}
                  alt="Logo"
                  className="h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12"
                />
                <h1 className="text-lg font-bold text-white sm:text-xl md:text-2xl">
                  Aerocomp
                </h1>
              </div>
            </div>

            {/* Tabs */}
            <div className="mb-4 flex gap-2 sm:mb-5 md:mb-6">
              <div
                className={`relative flex-1 cursor-pointer py-2.5 text-center text-xs font-semibold transition-all sm:py-3 sm:text-sm md:py-3.5 md:text-base ${
                  activeSection === 'book' ? 'text-[#FFBD59]' : 'text-gray-400'
                }`}
                onClick={() => handleSectionChange('book')}
              >
                <span className="hidden sm:inline">
                  Book a Free Appointment
                </span>
                <span className="sm:hidden">Book Appointment</span>
                <div
                  className={`absolute bottom-0 left-0 h-0.5 w-full transition-all sm:h-1 ${
                    activeSection === 'book' ? 'bg-[#FFBD59]' : 'bg-gray-700'
                  }`}
                />
              </div>

              <div
                className={`relative flex-1 cursor-pointer py-2.5 text-center text-xs font-semibold transition-all sm:py-3 sm:text-sm md:py-3.5 md:text-base ${
                  activeSection === 'email' ? 'text-[#FFBD59]' : 'text-gray-400'
                }`}
                onClick={() => handleSectionChange('email')}
              >
                Send us an Email
                <div
                  className={`absolute bottom-0 left-0 h-0.5 w-full transition-all sm:h-1 ${
                    activeSection === 'email' ? 'bg-[#FFBD59]' : 'bg-gray-700'
                  }`}
                />
              </div>
            </div>

            {/* Content */}
            <div className="transition-all duration-500 ease-in-out">
              {activeSection === 'book' ? (
                !showForm ? (
                  <div className="animate-fadeIn grid grid-cols-1 gap-4 sm:gap-5 md:gap-6 lg:grid-cols-2">
                    {/* Calendar Section */}
                    <div className="flex flex-col items-center justify-start">
                      <AppointmentCalendar onDateClick={handleDateClick} />
                    </div>
                    {/* Schedule Section */}
                    <div className="flex flex-col items-center justify-start">
                      <div className="sticky top-4 w-full rounded-xl border border-gray-800 bg-gray-900/60 p-4 sm:p-5">
                        <div className="mb-4 text-center">
                          <h2 className="text-base font-bold text-[#FFBD59] sm:text-lg md:text-xl">
                            Select your Schedule!
                          </h2>
                          <p className="mt-2 text-xs text-gray-400 sm:text-sm">
                            Pick a time that works for you—we&apos;ll be there!
                          </p>
                        </div>

                        {!selectedDate ? (
                          <div className="py-8 text-center text-sm text-gray-500">
                            Please select a date from the calendar
                          </div>
                        ) : loading ? (
                          <div className="py-8 text-center text-sm text-[#FFBD59]">
                            Loading available slots...
                          </div>
                        ) : timeSlots.length === 0 ? (
                          <div className="py-8 text-center text-sm text-gray-500">
                            No slots available for this date.
                          </div>
                        ) : (
                          <div className="animate-fadeIn">
                            <div className="mb-4 grid max-h-[25vh] grid-cols-1 gap-2 overflow-y-auto pr-2 sm:grid-cols-2 [&::-webkit-scrollbar-thumb]:rounded-md [&::-webkit-scrollbar-thumb]:bg-[#FFBD59] [&::-webkit-scrollbar-thumb]:hover:bg-[#F0AD4E] [&::-webkit-scrollbar-track]:rounded-md [&::-webkit-scrollbar-track]:bg-gray-800 [&::-webkit-scrollbar]:w-2">
                              {timeSlots.map((slot) => (
                                <button
                                  key={slot.id}
                                  onClick={() =>
                                    handleSlotClick(slot.id, slot.status)
                                  }
                                  disabled={slot.status === 'booked'}
                                  className={`rounded-lg px-3 py-3 text-xs font-semibold transition-all sm:text-sm ${
                                    selectedSlot === slot.id
                                      ? 'scale-105 bg-[#FFBD59] text-black shadow-lg'
                                      : slot.status === 'booked'
                                        ? 'cursor-not-allowed bg-gray-800 text-gray-600 opacity-50'
                                        : 'border border-gray-700 bg-gray-800/80 text-white hover:bg-gray-700'
                                  }`}
                                >
                                  {slot.time}
                                </button>
                              ))}
                            </div>

                            <button
                              onClick={handleContinue}
                              disabled={!selectedSlot || prebooking}
                              className={`w-full rounded-lg py-3 text-sm font-bold transition-all sm:text-base ${
                                selectedSlot && !prebooking
                                  ? 'bg-[#FFBD59] text-black shadow-lg hover:scale-105 hover:bg-[#F0AD4E]'
                                  : 'cursor-not-allowed bg-gray-800 text-gray-600'
                              }`}
                            >
                              {prebooking ? 'Checking slot...' : 'Continue'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="animate-fadeIn mx-auto max-w-4xl">
                    <div className="rounded-xl border border-gray-800 bg-gray-900/80 p-5 sm:p-6 md:p-7">
                      <div className="mb-5 flex items-center justify-between border-b border-gray-700 pb-4">
                        <h2 className="text-base font-bold text-white sm:text-lg md:text-xl">
                          Personal Details
                        </h2>
                        <button
                          onClick={handleGoBack}
                          className="flex items-center gap-2 text-xs text-gray-400 transition hover:text-white sm:text-sm"
                        >
                          <ArrowLeft size={16} />
                          Go Back
                        </button>
                      </div>

                      <div className="space-y-5">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            className="h-8 w-full  rounded-lg border border-gray-700 bg-gray-800/80 p-3 text-sm text-white placeholder-gray-500 transition focus:border-[#FFBD59] focus:outline-none"
                            placeholder="First Name"
                            required
                          />
                          <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            className="h-8 w-full  rounded-lg border border-gray-700 bg-gray-800/80 p-3 text-sm text-white placeholder-gray-500 transition focus:border-[#FFBD59] focus:outline-none"
                            placeholder="Last Name"
                            required
                          />
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="h-8 w-full rounded-lg border  border-gray-700 bg-gray-800/80 p-3 text-sm text-white placeholder-gray-500 transition focus:border-[#FFBD59] focus:outline-none"
                            placeholder="Email Address"
                            required
                          />
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="h-8 w-full rounded-lg border  border-gray-700 bg-gray-800/80 p-3 text-sm text-white placeholder-gray-500 transition focus:border-[#FFBD59] focus:outline-none"
                            placeholder="Contact No."
                            required
                          />
                        </div>

                        <div>
                          <h3 className="mb-3 border-b border-gray-700 pb-2 text-base font-bold text-white sm:text-lg">
                            Organization Details
                          </h3>
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <input
                              type="text"
                              name="company"
                              value={formData.company}
                              onChange={handleInputChange}
                              className="h-8 w-full rounded-lg  border border-gray-700 bg-gray-800/80 p-3 text-sm text-white placeholder-gray-500 transition focus:border-[#FFBD59] focus:outline-none"
                              placeholder="Company Name"
                              required
                            />
                            <input
                              type="text"
                              name="industry"
                              value={formData.industry}
                              onChange={handleInputChange}
                              className="h-8 w-full rounded-lg  border border-gray-700 bg-gray-800/80 p-3 text-sm text-white placeholder-gray-500 transition focus:border-[#FFBD59] focus:outline-none"
                              placeholder="Industry Field"
                              required
                            />
                            <input
                              type="email"
                              name="companyEmail"
                              value={formData.companyEmail}
                              onChange={handleInputChange}
                              className="h-8 w-full rounded-lg border  border-gray-700 bg-gray-800/80 p-3 text-sm text-white placeholder-gray-500 transition focus:border-[#FFBD59] focus:outline-none"
                              placeholder="Company Email Address"
                              required
                            />
                            <input
                              type="tel"
                              name="companyPhone"
                              value={formData.companyPhone}
                              onChange={handleInputChange}
                              className="h-8 w-full rounded-lg border  border-gray-700 bg-gray-800/80 p-3 text-sm text-white placeholder-gray-500 transition focus:border-[#FFBD59] focus:outline-none"
                              placeholder="Contact No."
                              required
                            />
                          </div>
                        </div>

                        <div>
                          <h3 className="mb-3 border-b border-gray-700 pb-2 text-base font-bold text-white sm:text-lg">
                            Notes
                          </h3>
                          <textarea
                            name="notes"
                            value={formData.notes}
                            onChange={handleInputChange}
                            className="h-16 w-full resize-none rounded-lg border border-gray-700 bg-gray-800/80 p-3 text-sm text-white placeholder-gray-500 transition focus:border-[#FFBD59] focus:outline-none"
                            placeholder="Add any additional notes or requirements..."
                            rows={4}
                          />
                        </div>

                        <div className="flex gap-3 pt-2">
                          <button
                            onClick={handleGoBack}
                            className="flex-1 rounded-lg border border-gray-700 py-3 text-sm font-semibold text-white transition hover:bg-gray-800"
                          >
                            Go Back to Schedule
                          </button>
                          <button
                            onClick={handleFormSubmit}
                            disabled={submitting}
                            className={`flex-1 rounded-lg py-3 text-sm font-bold transition ${
                              submitting
                                ? 'cursor-not-allowed bg-gray-800 text-gray-600'
                                : 'bg-[#FFBD59] text-black hover:scale-105 hover:bg-[#F0AD4E]'
                            }`}
                          >
                            {submitting ? (
                              <span className="flex items-center justify-center gap-2">
                                <svg
                                  className="h-4 w-4 animate-spin"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                                Processing...
                              </span>
                            ) : (
                              'Continue'
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              ) : (
                <div className="animate-fadeIn mx-auto max-w-6xl">
                  <div className="relative w-full bg-transparent px-0 py-6 text-left sm:py-8 md:py-10">
                    {/* Decorative elements */}
                    <div className="absolute right-0 top-0 h-32 w-32 rounded-full bg-[#FFBD59]/10 blur-3xl"></div>
                    <div className="absolute bottom-0 left-0 h-32 w-32 rounded-full bg-[#FFBD59]/10 blur-3xl"></div>

                    <section className="relative w-full overflow-hidden bg-transparent py-10 text-white">
                      <div className="mx-auto flex w-full flex-col-reverse items-center gap-5 px-4 sm:px-6 md:flex-row md:gap-5 lg:gap-5">
                        {/* LEFT CONTENT */}
                        <div className="relative z-10 max-w-md text-center md:text-left">
                          <div className="mb-6">
                            <h2 className="mb-3 text-2xl font-bold tracking-tighter text-[#FFBD59] sm:text-3xl md:text-2xl lg:text-2xl">
                              We&apos;d love to hear from you!
                            </h2>

                            <p className="mx-auto max-w-sm text-sm leading-relaxed tracking-tight text-white sm:text-base md:text-sm lg:text-sm">
                              We’re always eager to hear from you! Share your
                              thoughts, feedback, or inquiries by clicking the
                              button below and let’s start the conversation
                              today.
                            </p>
                          </div>

                          <button
                            onClick={handleOpenGmail}
                            className="mx-auto inline-flex items-center justify-center gap-3 rounded-md bg-[#FFBD59] px-8 py-3 text-sm font-normal text-black shadow-lg transition-all hover:scale-105 hover:bg-[#F0AD4E] md:mx-0 md:px-6 md:py-2.5 md:text-xs lg:px-8 lg:py-3 lg:text-sm"
                          >
                            <Mail size={20} />
                            Send us an Email
                          </button>

                          <p className="mt-4 text-xs text-gray-500 sm:text-sm md:text-xs">
                            Or reach us directly at{' '}
                            <a
                              href="mailto:aerocompproject@gmail.com"
                              className="text-[#FFBD59] hover:underline"
                            >
                              aerocompproject@gmail.com
                            </a>
                          </p>
                        </div>

                        {/* RIGHT IMAGE */}
                        <div className="relative z-10 -mt-8 flex items-start justify-center sm:-mt-12 md:-mt-16 lg:-mt-20 xl:-mt-14">
                          <img
                            src={soldier}
                            alt="Soldier"
                            className=" relative z-10  w-[300px] object-contain sm:w-[300px] md:w-[320px]   lg:w-[380px] xl:w-[550px] 2xl:w-[500px]"
                          />
                        </div>
                      </div>
                    </section>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Booking Receipt Modal */}
      {showReceipt && bookingData ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/80 p-3 backdrop-blur-md sm:p-4"
          onClick={() => setShowReceipt(false)}
        >
          <div
            className="animate-fadeIn relative my-8 w-full max-w-[90%] rounded-lg border border-gray-700 bg-gradient-to-b from-[#2a2a2a] to-[#1a1a1a] shadow-2xl sm:max-w-[420px] md:max-w-[480px]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowReceipt(false)}
              className="absolute -right-2 -top-2 z-10 rounded-full border border-gray-600 bg-gray-800 p-2 text-white shadow-lg transition hover:bg-gray-700 sm:-right-3 sm:-top-3 sm:p-2.5"
            >
              <X size={18} />
            </button>

            <div className="max-h-[85vh] overflow-y-auto p-5 text-center sm:p-6 md:p-7 [&::-webkit-scrollbar-thumb]:rounded-md [&::-webkit-scrollbar-thumb]:bg-[#FFBD59] [&::-webkit-scrollbar-thumb]:hover:bg-[#F0AD4E] [&::-webkit-scrollbar-track]:rounded-md [&::-webkit-scrollbar-track]:bg-gray-800 [&::-webkit-scrollbar]:w-2">
              <div className="mb-4 sm:mb-5">
                <h2 className="text-lg font-bold text-[#FFBD59] sm:text-xl">
                  Booking Receipt
                </h2>
                <p className="mt-2 text-xs text-gray-400 sm:text-sm">
                  Let&apos;s talk about what you need for Free!
                </p>
              </div>

              <div className="mb-4 rounded-xl bg-gradient-to-r from-[#FFC94A] to-[#FFBD59] px-4 py-4 shadow-lg sm:mb-5 sm:px-5 sm:py-5">
                <p className="text-xl font-extrabold text-black sm:text-2xl">
                  {bookingData.date}
                </p>
                <p className="mt-1 text-xs font-semibold text-black/80 sm:text-sm">
                  {bookingData.time}
                </p>
              </div>

              <div className="mb-3 rounded-xl border border-gray-700 bg-gray-800/50 p-3 text-left sm:mb-4 sm:p-4">
                <h3 className="mb-2 text-xs font-bold text-[#FFBD59] sm:mb-3 sm:text-sm">
                  Personal Details
                </h3>
                <div className="space-y-1 text-xs sm:space-y-2 sm:text-sm">
                  <p className="font-semibold text-white">
                    {bookingData.firstName} {bookingData.lastName}
                  </p>
                  <p className="text-gray-400">{bookingData.phone}</p>
                  <p className="break-all text-gray-400">{bookingData.email}</p>
                </div>
              </div>

              <div className="mb-3 rounded-xl border border-gray-700 bg-gray-800/50 p-3 text-left sm:mb-4 sm:p-4">
                <h3 className="mb-2 text-xs font-bold text-[#FFBD59] sm:mb-3 sm:text-sm">
                  Company Details
                </h3>
                <div className="space-y-1 text-xs sm:space-y-2 sm:text-sm">
                  <p className="font-semibold text-white">
                    {bookingData.company}
                  </p>
                  <p className="text-gray-400">{bookingData.industry}</p>
                  <p className="break-all text-gray-400">
                    {bookingData.companyEmail}
                  </p>
                  <p className="text-gray-400">{bookingData.companyPhone}</p>
                </div>
              </div>

              {bookingData.notes ? (
                <div className="mb-4 rounded-xl border border-gray-700 bg-gray-800/50 p-3 text-left sm:mb-5 sm:p-4">
                  <h3 className="mb-2 text-xs font-bold text-[#FFBD59] sm:text-sm">
                    Notes
                  </h3>
                  <p className="whitespace-pre-wrap break-words text-xs text-gray-400 sm:text-sm">
                    {bookingData.notes}
                  </p>
                </div>
              ) : null}

              <div className="flex gap-2 pt-2 sm:gap-3">
                <button
                  onClick={() => setShowReceipt(false)}
                  disabled={booking}
                  className="flex-1 rounded-lg border border-gray-600 py-2.5 text-xs font-semibold text-gray-300 transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50 sm:py-3 sm:text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBookIt}
                  disabled={booking}
                  className="flex-1 rounded-lg bg-gradient-to-r from-[#FFC94A] to-[#FFBD59] py-2.5 text-xs font-bold text-black shadow-md transition hover:scale-105 hover:from-[#FFB233] hover:to-[#FFBE33] disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:scale-100 sm:py-3 sm:text-sm"
                >
                  {booking ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg
                        className="h-4 w-4 animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Booking...
                    </span>
                  ) : (
                    'Book it'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <BookingStatusModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        status={bookingStatus}
        message={statusMessage}
      />

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-in-out;
        }
      `}</style>
    </>
  );
}
